// LWI-1562 — the Pick and Pack handling unit as one declarative field list.
//
// Write keys are the shipment_line_attributes columns MSRController::PickAndPack
// already accepts; read keys also cover the legacy AddHandlingPartDialog's
// camelCase shape (qty, weight, containerType, length…) so units added by the
// classic page still display. Writing the snake shape means Final Details'
// totals / freight class (which read snake keys) count a new unit at once.

import {
  DEFAULT_WEIGHT_UOM,
  isInvalidWeightUom,
  normalizeWeightUom,
  toPounds,
} from "./weightUom";

export const NEW_UNIT_KEY = "__new";

export const HU_GROUPS = [
  { key: "unit", label: "Unit" },
  { key: "weight", label: "Weight" },
  { key: "dimensions", label: "Dimensions" },
  { key: "dg", label: "Dangerous Goods", termKey: "dangerousGoods" },
];

export const DIMENSION_UOMS = [
  { value: "in", name: "in" },
  { value: "cm", name: "cm" },
];

export const TDG_OPTIONS = [
  { value: "No", name: "No" },
  { value: "Yes", name: "Yes" },
];

/** @type {Array<{key:string, read:string[], label:string, group:string, type?:string, options?:string, required?:boolean}>} */
export const HU_FIELDS = [
  {
    key: "description",
    read: ["description", "descriptionOfArticles"],
    label: "Description",
    group: "unit",
    required: true,
  },
  {
    key: "item_uom",
    read: ["item_uom", "containerType", "containter_type"],
    label: "Container Type",
    group: "unit",
    type: "select",
    options: "containerTypes",
  },
  {
    key: "quantity_fulfilled",
    read: ["quantity_fulfilled", "qty"],
    label: "Qty",
    group: "unit",
    type: "number",
    required: true,
  },
  {
    key: "weight_fulfilled",
    read: ["weight_fulfilled", "weight"],
    label: "Weight (per unit)",
    group: "weight",
    type: "number",
  },
  {
    key: "weight_uom",
    read: ["weight_uom", "uom"],
    label: "Weight UOM",
    group: "weight",
    type: "select",
    options: "weightUoms",
  },
  {
    key: "dimension_length",
    read: ["dimension_length", "length"],
    label: "Length",
    group: "dimensions",
    type: "number",
  },
  {
    key: "dimension_width",
    read: ["dimension_width", "width"],
    label: "Width",
    group: "dimensions",
    type: "number",
  },
  {
    key: "dimension_height",
    read: ["dimension_height", "height"],
    label: "Height",
    group: "dimensions",
    type: "number",
  },
  {
    key: "dimension_length_uom",
    read: ["dimension_length_uom", "dimensionsUOM", "dimensiosUOM"],
    label: "Dimensions UOM",
    group: "dimensions",
    type: "select",
    options: "dimensionUoms",
  },
  {
    key: "stackability_factor",
    read: ["stackability_factor", "stackingHeight"],
    label: "Stacking Height",
    group: "dimensions",
    type: "number",
  },
  {
    key: "tdg",
    read: ["tdg"],
    label: "TDG (dangerous goods)",
    termKey: "tdgFull",
    group: "dg",
    type: "select",
    options: "tdg",
  },
];

export const huFieldByKey = (key) => HU_FIELDS.find((f) => f.key === key);

export const huValue = (row, key, fallback = "") => {
  const f = huFieldByKey(key);
  const keys = f ? f.read : [key];
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && v !== "")
      return key === "weight_uom" ? normalizeWeightUom(v) : v;
  }
  return fallback;
};

/** Stable identity for a row: client key, else DB id, else line id. */
export const unitKey = (row) => {
  const k = row?.__key ?? row?.id ?? row?.line_id;
  return k === undefined || k === null ? "" : String(k);
};

let seq = 0;
const nextKey = () => `hu-${Date.now().toString(36)}-${(seq += 1)}`;

/** Give every row a `__key` (idempotent) so delete / edit never key on `id`, which unsaved rows lack. */
export const withKeys = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((r) =>
    r && r.__key ? r : { ...r, __key: unitKey(r) || nextKey() }
  );

export const newUnit = () => ({
  __key: nextKey(),
  description: "",
  item_uom: "",
  quantity_fulfilled: "",
  weight_fulfilled: "",
  weight_uom: DEFAULT_WEIGHT_UOM,
  dimension_length: "",
  dimension_width: "",
  dimension_height: "",
  dimension_length_uom: "in",
  stackability_factor: "",
  tdg: "No",
});

/**
 * Apply a patch of write keys. The dimension UOM is one value on the form but
 * three columns in the table; keep all three in step.
 */
export const applyUnitPatch = (row, patch) => {
  const next = { ...row, ...patch };
  if ("dimension_length_uom" in patch) {
    next.dimension_width_uom = patch.dimension_length_uom;
    next.dimension_height_uom = patch.dimension_length_uom;
  }
  return next;
};

/**
 * Field labels a unit still needs (client rule: Description and Qty > 0).
 * LWI-1593 — a leftover non-weight UOM ("Gram", "Each"…) counts too: the
 * server refuses it, so the unit has to be fixed before Pick and Pack saves.
 */
export const unitMissing = (row) => {
  const missing = [];
  if (String(huValue(row, "description", "")).trim() === "")
    missing.push("Description");
  if (!(Number(huValue(row, "quantity_fulfilled", 0)) > 0)) missing.push("Qty");
  if (isInvalidWeightUom(huValue(row, "weight_uom", "")))
    missing.push("Weight UOM");
  return missing;
};

/** Unit weight in pounds (LWI-1593: KG units convert; totals are shown in lb). */
export const unitWeightLb = (row) =>
  toPounds(huValue(row, "weight_fulfilled", 0), huValue(row, "weight_uom", ""));

/** Pieces = Σ qty; weight = Σ weight(lb) × qty — the same rule Final Details shows. */
export const unitTotals = (rows = []) =>
  (Array.isArray(rows) ? rows : []).reduce(
    (t, u) => {
      const qty = parseFloat(huValue(u, "quantity_fulfilled", 0)) || 0;
      const w = unitWeightLb(u);
      return {
        units: t.units + 1,
        pieces: t.pieces + qty,
        weight: t.weight + w * qty,
      };
    },
    { units: 0, pieces: 0, weight: 0 }
  );

/**
 * Density-based freight class (lb per ft³), the Details page's lookup; KG units
 * convert (LWI-1593). LWI-1624 — lived on the Final Details section; now saved
 * with every Pick and Pack save (pickPackPayload).
 */
export const calculateFreightClass = (handlingUnits = []) => {
  let totalWeight = 0;
  let totalVolume = 0;
  (handlingUnits || []).forEach((u) => {
    const weight = unitWeightLb(u);
    const volume =
      ((parseFloat(u.dimension_length) || 0) *
        (parseFloat(u.dimension_width) || 0) *
        (parseFloat(u.dimension_height) || 0)) /
      1728;
    totalWeight += weight;
    totalVolume += volume;
  });
  if (totalVolume === 0) return "N/A";
  const d = totalWeight / totalVolume;
  if (d > 50) return "50";
  if (d > 35) return "55";
  if (d > 30) return "60";
  if (d > 22.5) return "65";
  if (d > 15) return "70";
  if (d > 13.5) return "77.5";
  if (d > 12) return "85";
  if (d > 10.5) return "92.5";
  if (d > 9) return "100";
  if (d > 8) return "110";
  if (d > 7) return "125";
  if (d > 6) return "150";
  if (d > 5) return "175";
  if (d > 4) return "200";
  if (d > 3) return "250";
  if (d > 2) return "300";
  if (d > 1) return "400";
  return "500";
};

export const dimensionsLabel = (row) => {
  const l = huValue(row, "dimension_length", "");
  const w = huValue(row, "dimension_width", "");
  const h = huValue(row, "dimension_height", "");
  if (l === "" && w === "" && h === "") return "";
  return `${l || "–"} × ${w || "–"} × ${h || "–"} ${huValue(
    row,
    "dimension_length_uom",
    ""
  )}`.trim();
};
